import {gql} from '@apollo/client'

export const GET_CAMPAIGNS = gql`
  query Campaigns($status: String, $includeArchived: Boolean, $search: String) {
    campaigns(status: $status, includeArchived: $includeArchived, search: $search) {
      id
      name
      description
      status
      scheduleType
      scheduledTime
      recurringRule
      enabledChannels
      totalTargets
      sentCount
      deliveredCount
      failedCount
      statistics
      metadata
      owner {
        id
        firstName
        lastName
        email
      }
      template {
        id
        name
        channel
        allChannels
      }
    }
  }
`

export const GET_CAMPAIGN = gql`
  query Campaign($id: ID!) {
    campaign(id: $id) {
      id
      name
      description
      status
      scheduleType
      scheduledTime
      recurringRule
      enabledChannels
      targetSegment
      metadata
      totalTargets
      sentCount
      deliveredCount
      failedCount
      statistics
      lastRunAt
      lastRunTaskId
      owner {
        id
        firstName
        lastName
        email
      }
      template {
        id
        name
        channel
        subject
        description
        allChannels
        variants {
          id
          channel
          subject
          bodyText
          bodyHtml
          isDefault
        }
      }
    }
  }
`

export const GET_CAMPAIGN_TARGET_CONTACT_IDS = gql`
  query CampaignTargetContactIds($campaignId: ID!) {
    campaignTargets(campaignId: $campaignId, page: 1, pageSize: 99999) {
      items {
        contact {
          id
        }
      }
    }
  }
`

export const GET_CAMPAIGN_TARGETS = gql`
  query CampaignTargets($campaignId: ID!, $page: Int, $pageSize: Int) {
    campaignTargets(campaignId: $campaignId, page: $page, pageSize: $pageSize) {
      items {
        id
        state
        lastChannel
        lastAttemptedAt
        errorCount
        metadata
        contact {
          id
          firstName
          lastName
          email
          phone
        }
      }
      totalCount
      totalPages
      page
    }
  }
`

export const GET_CAMPAIGN_LOGS = gql`
  query CampaignLogs($campaignId: ID, $page: Int, $pageSize: Int) {
    campaignLogs(campaignId: $campaignId, page: $page, pageSize: $pageSize) {
      items {
        id
        status
        channel
        providerMessageId
        errorMessage
        createdAt
        payload
        contact {
          id
          firstName
          lastName
          email
        }
      }
      totalCount
      totalPages
      page
    }
  }
`

export const GET_MESSAGE_TEMPLATES = gql`
  query MessageTemplates($channel: String) {
    messageTemplates(channel: $channel) {
      id
      name
      description
      channel
      subject
      body
      variables
      metadata
      isActive
      createdAt
      updatedAt
      isBlueprint
      allChannels
      owner {
        id
        firstName
        lastName
        email
      }
      organization {
        id
        name
      }
      variants {
        id
        channel
        subject
        bodyText
        bodyHtml
        isDefault
      }
    }
  }
`

export const GET_MESSAGE_TEMPLATE_WITH_VARIANTS = gql`
  query MessageTemplateWithVariants($id: ID!) {
    messageTemplate(id: $id) {
      id
      name
      description
      templateType
      channel
      subject
      body
      variables
      metadata
      isActive
      isBlueprint
      allChannels
      variants {
        id
        channel
        subject
        bodyText
        bodyHtml
        whatsappTemplateId
        whatsappLanguage
        whatsappTemplateParams
        smsMaxLength
        isDefault
        variables
        metadata
        createdAt
        updatedAt
      }
    }
  }
`

export const GET_CHANNEL_VARIANT = gql`
  query ChannelVariant($id: ID!) {
    channelVariant(id: $id) {
      id
      channel
      subject
      bodyText
      bodyHtml
      whatsappTemplateId
      whatsappLanguage
      whatsappTemplateParams
      smsMaxLength
      isDefault
      variables
      metadata
      template {
        id
        name
      }
    }
  }
`

// ─── Automation Template Queries ─────────────────────────────────────────────

export const GET_AUTOMATION_TEMPLATES = gql`
  query GetAutomationTemplates($category: String, $search: String) {
    automationTemplates(category: $category, search: $search) {
      id
      name
      description
      category
      triggerType
      triggerConfig
      channel
      delayDays
      sendHour
      sendMinute
      createdAt
      suggestedTemplate {
        id
        name
        subject
        channel
      }
    }
  }
`

// ─── Automation Queries ───────────────────────────────────────────────────────

export const AUTOMATION_FIELDS = `
  id
  name
  description
  scope
  triggerType
  triggerConfig
  channel
  delayDays
  sendHour
  sendMinute
  conditions
  isActive
  lastRunAt
  logCount
  sentCount
  createdAt
  updatedAt
  template {
    id
    name
    channel
  }
  owner {
    id
    firstName
    lastName
  }
`

export const GET_AUTOMATIONS = gql`
  query GetAutomations($isActive: Boolean, $triggerType: String, $channel: String, $search: String) {
    automations(isActive: $isActive, triggerType: $triggerType, channel: $channel, search: $search) {
      ${AUTOMATION_FIELDS}
    }
  }
`

export const GET_AUTOMATION = gql`
  query GetAutomation($id: ID!) {
    automation(id: $id) {
      ${AUTOMATION_FIELDS}
    }
  }
`

export const PREVIEW_AUTOMATION_CONTACTS = gql`
  query PreviewAutomationContacts($triggerType: String!, $triggerConfig: GenericScalar, $scope: String) {
    previewAutomationContacts(triggerType: $triggerType, triggerConfig: $triggerConfig, scope: $scope) {
      contactCount
    }
  }
`

export const GET_AUTOMATION_SCHEDULED_TODAY = gql`
  query GetAutomationScheduledToday($automationId: ID!) {
    automationScheduledToday(automationId: $automationId) {
      contactId
      firstName
      lastName
      email
      phone
      dateBirth
      alreadySent
    }
  }
`

export const GET_AUTOMATION_LOGS = gql`
  query GetAutomationLogs($automationId: ID!, $status: String) {
    automationLogs(automationId: $automationId, status: $status) {
      id
      status
      channel
      errorMessage
      sentAt
      contact {
        id
        firstName
        lastName
        email
        phone
      }
      policy {
        id
      }
    }
  }
`
